import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { sessionService } from 'redux-react-session'

import { requestLogin } from '@actions/user'
import { setToken } from '@services/sm-api.js'
import smAuth from '@services/sm-auth.js'
import { openModal } from '@actions/modal'
import LoginModal from '@components/LoginModal'
import { openGooglePopup } from '../../../utils/googlePopup'
import { FIRST_ACCESS_ERROR, FIRST_ACCESS_SENT, checkFirstAccess } from '../firstAccess'
import { loginWithGoogleToken } from '@actions/user'

import V2Layout from '../V2Layout'
import V2CartAside from '../V2CartAside'
import V2Stepper from '../V2Stepper'

// Identificação V2 — porte fiel do design. Regras preservadas (MATRIZ):
// L-01/L-02 (obrigatórios, mesmas mensagens), L-03 (erro da API por campo),
// L-04 (bloqueio → LoginModal), L-05..L-07 (GA, persitSession, redirect),
// L-21..L-23 (Google via popup), I-02 (eventos por caminho).
// Transição: sucesso segue para /steps/atualizacao (V1) até a etapa de Resumo V2 existir.

const label = { display: 'block', margin: '18px 0 8px', fontSize: '12px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6B6664' }
const input = { width: '100%', height: '52px', padding: '0 16px', border: '1.5px solid #E3DBD7', borderRadius: '12px', fontSize: '16px', color: '#373535', outline: 'none', boxSizing: 'border-box' }
const fieldError = { margin: '6px 0 0', fontSize: '13px', color: '#C2262B' }

export default function V2Identification() {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [firstAccessNotice, setFirstAccessNotice] = useState('')
  // Discovery (severidade alta): não exigir login de quem já tem sessão ativa —
  // oferece "Continuar como" e mantém o formulário atrás de "Entrar com outra conta"
  const sessionUser = useSelector(state => (state.sessionReducer && state.sessionReducer.authenticated && state.sessionReducer.user) || null)
  const sessionName = sessionUser && (sessionUser.name || sessionUser.full_name)
  const [switchAccount, setSwitchAccount] = useState(false)

  const _continueAsSession = async () => {
    // sem re-login não há setToken — o header do axios pode estar com o token do
    // boot (stale, família do achado #17); sincroniza com a sessão ativa antes
    try {
      const session = await sessionService.loadSession()
      if (session && session.token) {
        setToken(session.token)
        smAuth.setToken(session.token)
      }
    } catch (e) {}
    // mesmo destino do login (L-05: revisão cadastral obrigatória)
    sessionStorage.setItem('smd_v2_flow', '1')
    navigate('/v2/steps/atualizacao')
  }

  const _handleLogin = () => {
    // L-01/L-02: mesmas mensagens do V1
    const next = {}
    if (!email) next.email = 'Por favor, preencha o seu email'
    if (!password) next.password = 'Por favor, preencha a sua senha'
    setErrors(next)
    if (Object.keys(next).length) return

    // L-06: evento GA antes da chamada
    window.ga && window.ga('send', { hitType: 'event', eventCategory: 'compra', eventAction: 'click', eventLabel: 'logou_4.1' })

    setSubmitting(true)
    setFirstAccessNotice('')
    // regra do V1 (LoginModal): conta sem senha recebe o e-mail de cadastro de senha
    checkFirstAccess(email).then(firstAccess => {
      if (!firstAccess) return _login()
      setSubmitting(false)
      if (firstAccess === 'sent') setFirstAccessNotice(FIRST_ACCESS_SENT(email))
      else setErrors({ email: FIRST_ACCESS_ERROR })
    })
  }

  const _login = () => {
    // L-07: persitSession forçado
    dispatch(requestLogin({ email, password, persitSession: true })).then(response => {
      setSubmitting(false)
      if (response && response.message) {
        // L-03: erro da API injetado no campo correspondente
        setErrors({
          email: response.message.type === 'email' ? response.message.error : '',
          password: response.message.type === 'password' ? response.message.error : '',
        })
        // L-04: bloqueio por excesso de tentativas
        if (response.message.type === 'blocked') {
          dispatch(openModal(<LoginModal active="login" blocked embedded />))
        }
      } else {
        // L-05: revisão cadastral obrigatória após login
        sessionStorage.setItem('smd_v2_flow', '1')
        navigate('/v2/steps/atualizacao')
      }
    })
  }

  const _handleGoogle = () => {
    // L-21..L-23: mesmo utilitário de popup/polling do V1
    openGooglePopup(
      (token, user) => {
        dispatch(loginWithGoogleToken(token, user)).then(() => {
          sessionStorage.setItem('smd_v2_flow', '1')
          navigate('/v2/steps/atualizacao')
        })
      },
      err => console.error('Erro Google login:', err)
    )
  }

  const _handleRegister = () => {
    window.ga && window.ga('send', { hitType: 'event', eventCategory: 'compra', eventAction: 'click', eventLabel: 'novo_usuario_4' })
    // rodada 3 (09/09, item 20): o formulário V1 (37 regras I-*) agora abre no chrome V2
    navigate('/v2/steps/cadastro')
  }

  return (
    <V2Layout>
      <V2Stepper current={2} />
      <div className="v2-step-grid" style={{ maxWidth: '1200px', margin: '0 auto', padding: '56px 32px 110px', display: 'grid', gridTemplateColumns: '1fr 340px', gap: '40px', alignItems: 'start' }}>
        <div>
        <span className="v2-eyebrow">Etapa 3 de 5</span>
        <h1 style={{ margin: '8px 0 0', fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '34px', lineHeight: '1.15', letterSpacing: '-0.7px', color: '#373535' }}>
          Identificação
        </h1>
        <p style={{ margin: '12px 0 0', maxWidth: '560px', fontSize: '16px', lineHeight: '1.6', color: '#6B6664' }}>
          Entre na sua conta SM para que os dados de cobrança já venham preenchidos no resumo do pedido.
        </p>

        {sessionName && !switchAccount ? (
          <div id="v2-login-continue-card" style={{ marginTop: '28px', maxWidth: '460px', border: '1px solid #EFE7E3', borderRadius: '18px', padding: '26px', background: '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <span style={{ flex: 'none', width: '44px', height: '44px', borderRadius: '50%', background: '#D8262C', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '16px' }}>
                {sessionName.trim().split(/\s+/).slice(0, 2).map(p => p[0]).join('').toUpperCase()}
              </span>
              <div style={{ minWidth: 0 }}>
                <p style={{ margin: 0, fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '16px', color: '#373535' }}>{sessionName}</p>
                {sessionUser.email && <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#6B6664', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sessionUser.email}</p>}
              </div>
            </div>
            <button
              id="v2-login-continue-as"
              type="button"
              onClick={_continueAsSession}
              style={{ marginTop: '20px', width: '100%', height: '52px', border: 'none', borderRadius: '12px', background: '#D8262C', color: '#fff', fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '15px', cursor: 'pointer' }}
            >
              Continuar como {sessionName.split(' ')[0]}
            </button>
            <button
              id="v2-login-switch-account"
              type="button"
              onClick={() => setSwitchAccount(true)}
              style={{ marginTop: '10px', width: '100%', height: '48px', border: '1.5px solid #E3DBD7', borderRadius: '12px', background: '#fff', fontSize: '14px', fontWeight: '700', color: '#6B6664', cursor: 'pointer' }}
            >
              Entrar com outra conta
            </button>
          </div>
        ) : (
        <div style={{ marginTop: '28px', maxWidth: '460px', border: '1px solid #EFE7E3', borderRadius: '18px', padding: '26px', background: '#fff' }}>
          {firstAccessNotice && (
            <div id="v2-login-notice" style={{ marginBottom: '14px', padding: '12px 14px', borderRadius: '12px', background: '#E8F4EE', color: '#16603C', fontSize: '14px', fontWeight: '700' }}>
              {firstAccessNotice}
            </div>
          )}
          <label htmlFor="v2-login-email" style={{ ...label, margin: '0 0 8px' }}>E-mail</label>
          <input
            id="v2-login-email"
            className="v2d-input"
            type="email"
            placeholder="voce@email.com"
            autoComplete="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            style={input}
          />
          {errors.email && <p style={fieldError}>{errors.email}</p>}

          <label htmlFor="v2-login-password" style={label}>Senha</label>
          <input
            id="v2-login-password"
            className="v2d-input"
            type="password"
            placeholder="••••••••"
            autoComplete="current-password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && _handleLogin()}
            style={input}
          />
          {errors.password && <p style={fieldError}>{errors.password}</p>}

          <button
            id="v2-login-submit"
            type="button"
            disabled={submitting}
            onClick={_handleLogin}
            style={{ marginTop: '22px', width: '100%', height: '52px', border: 'none', borderRadius: '12px', background: '#D8262C', color: '#fff', fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '15px', cursor: 'pointer', opacity: submitting ? 0.7 : 1 }}
          >
            Entrar e continuar
          </button>

          <div style={{ margin: '20px 0', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ flex: 1, height: '1px', background: '#EFE7E3' }} />
            <span style={{ fontSize: '12px', color: '#8E8783' }}>ou</span>
            <span style={{ flex: 1, height: '1px', background: '#EFE7E3' }} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button
              id="v2-login-google"
              type="button"
              onClick={_handleGoogle}
              style={{ height: '50px', border: '1.5px solid #E3DBD7', borderRadius: '12px', background: '#fff', fontSize: '14px', fontWeight: '700', color: '#373535', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              Entrar com Google
            </button>
            <button
              id="v2-login-register"
              type="button"
              onClick={_handleRegister}
              style={{ height: '50px', border: 'none', borderRadius: '12px', background: '#FAF7F5', fontSize: '14px', fontWeight: '700', color: '#6B6664', cursor: 'pointer' }}
            >
              Não tenho cadastro — criar conta
            </button>
          </div>
        </div>
        )}
        </div>
        <V2CartAside />
      </div>
    </V2Layout>
  )
}
